/**
 * Client-side mirror of the Mirror Guardian backend bounds (§2). Validating
 * here BEFORE submit gives the admin immediate feedback and blocks an obviously
 * bad save; the backend still re-validates and a 400 GUARDIAN_BOUNDS is
 * surfaced verbatim if the two ever disagree.
 *
 * Bounds (must match `buildMirrorGuardianRouter`'s PUT validation):
 *   - evidence_floor_decline, evidence_band_hedge, likeness_revision_floor ∈ [0,1]
 *   - evidence_floor_decline ≤ evidence_band_hedge   (monotonic decline→hedge)
 *   - max_history_turns integer in 1..50
 */

import { GuardianConfig } from './api';

export interface GuardianDraft {
  evidence_floor_decline: number;
  evidence_band_hedge: number;
  likeness_revision_floor: number;
  max_history_turns: number;
}

/** Field-keyed validation errors; empty object === valid. */
export type GuardianErrors = Partial<Record<keyof GuardianDraft, string>>;

const inUnit = (n: number) => Number.isFinite(n) && n >= 0 && n <= 1;

export function validateGuardianDraft(d: GuardianDraft): GuardianErrors {
  const errors: GuardianErrors = {};

  if (!inUnit(d.evidence_floor_decline)) {
    errors.evidence_floor_decline = 'Decline floor must be between 0 and 1.';
  }
  if (!inUnit(d.evidence_band_hedge)) {
    errors.evidence_band_hedge = 'Hedge threshold must be between 0 and 1.';
  }
  if (!inUnit(d.likeness_revision_floor)) {
    errors.likeness_revision_floor = 'Likeness revision floor must be between 0 and 1.';
  }

  // Monotonic: decline must not exceed hedge (only checked when both are sane).
  if (
    inUnit(d.evidence_floor_decline) &&
    inUnit(d.evidence_band_hedge) &&
    d.evidence_floor_decline > d.evidence_band_hedge
  ) {
    errors.evidence_band_hedge =
      'Hedge threshold must be greater than or equal to the decline floor.';
  }

  if (
    !Number.isInteger(d.max_history_turns) ||
    d.max_history_turns < 1 ||
    d.max_history_turns > 50
  ) {
    errors.max_history_turns = 'History window must be a whole number from 1 to 50.';
  }

  return errors;
}

export function isGuardianDraftValid(d: GuardianDraft): boolean {
  return Object.keys(validateGuardianDraft(d)).length === 0;
}

/** Extract just the editable draft fields from a loaded config. */
export function draftFromConfig(c: GuardianConfig): GuardianDraft {
  return {
    evidence_floor_decline: c.evidence_floor_decline,
    evidence_band_hedge: c.evidence_band_hedge,
    likeness_revision_floor: c.likeness_revision_floor,
    max_history_turns: c.max_history_turns,
  };
}

export function isDraftDirty(a: GuardianDraft, b: GuardianDraft): boolean {
  return (
    a.evidence_floor_decline !== b.evidence_floor_decline ||
    a.evidence_band_hedge !== b.evidence_band_hedge ||
    a.likeness_revision_floor !== b.likeness_revision_floor ||
    a.max_history_turns !== b.max_history_turns
  );
}
